if (!customElements.get('cart-free-gifts')) {
  class CartFreeGifts extends HTMLElement {
    #giftThreshold;
    #creditPercent;
    #maxGifts;
    #operationInProgress = false;
    #collectionHandle;
    #cartUpdateHandler = null;
    #dialogOpenHandler = null;
    #updateArrows = null;
    #observer = null;

    connectedCallback() {
      this.#giftThreshold = parseInt(this.dataset.giftThreshold, 10);
      this.#creditPercent = parseInt(this.dataset.creditPercent, 10);
      this.#maxGifts = parseInt(this.dataset.maxGifts, 10) || 3;
      this.#collectionHandle = this.dataset.collectionHandle;

      this.addEventListener('click', this.#handleClick.bind(this));

      this.#initArrowNavigation();

      const scrollEl = this.querySelector('.cart-free-gifts__scroll');
      if (scrollEl) scrollEl.scrollLeft = 0;

      this.#cartUpdateHandler = this.#handleCartUpdate.bind(this);
      document.addEventListener('cart:update', this.#cartUpdateHandler);

      this.#dialogOpenHandler = this.#handleDialogOpen.bind(this);
      document.addEventListener('dialog:open', this.#dialogOpenHandler);

      this.#sortGiftCards();

      // Remove any backordered / out-of-stock gifts already in the cart on load/refresh
      this.#removeFlaggedGifts();
    }

    disconnectedCallback() {
      if (this.#cartUpdateHandler) {
        document.removeEventListener('cart:update', this.#cartUpdateHandler);
      }
      if (this.#dialogOpenHandler) {
        document.removeEventListener('dialog:open', this.#dialogOpenHandler);
      }
    }

    #handleDialogOpen(event) {
      if (!event.target || !event.target.contains(this)) return;
      if (typeof this.#updateArrows !== 'function') return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.#updateArrows());
      });
    }

    #initArrowNavigation() {
      const getScrollEl = () => this.querySelector('.cart-free-gifts__scroll');
      const getScrollAmount = () => (getScrollEl()?.clientWidth || 0) * 0.8;

      const updateArrows = () => {
        const scrollEl = getScrollEl();
        const prevBtn = this.querySelector('.slideshow-control--previous');
        const nextBtn = this.querySelector('.slideshow-control--next');
        if (!scrollEl || !prevBtn || !nextBtn) return;
        const { scrollLeft, scrollWidth, clientWidth } = scrollEl;
        prevBtn.disabled = scrollLeft <= 2;
        nextBtn.disabled = scrollLeft >= scrollWidth - clientWidth - 2;
      };
      this.#updateArrows = updateArrows;

      // Delegated click — survives inner DOM replacement
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('.slideshow-control');
        if (!btn) return;
        e.stopPropagation();
        const scrollEl = getScrollEl();
        if (!scrollEl) return;
        const dir = btn.classList.contains('slideshow-control--previous') ? -1 : 1;
        scrollEl.scrollBy({ left: dir * getScrollAmount(), behavior: 'smooth' });
      });

      // Re-attach scroll/resize listeners when inner DOM changes
      const attachScrollListeners = (scrollEl) => {
        if (!scrollEl || scrollEl.dataset.listening) return;
        scrollEl.dataset.listening = '1';
        scrollEl.addEventListener('scroll', updateArrows, { passive: true });
        scrollEl.addEventListener('scrollend', updateArrows, { passive: true });
        new ResizeObserver(() => updateArrows()).observe(scrollEl);
      };

      this.#observer = new MutationObserver(() => {
        attachScrollListeners(getScrollEl());
        updateArrows();
        this.#sortGiftCards();
      });
      this.#observer.observe(this, { childList: true, subtree: true, characterData: true });

      // Initial scroll listener
      attachScrollListeners(getScrollEl());

      updateArrows();
    }

    async #handleClick(event) {
      const button = event.target.closest('[data-action]');
      if (!button || this.#operationInProgress) return;

      const action = button.dataset.action;
      const variantId = button.dataset.variantId;

      if (!variantId) return;

      this.#operationInProgress = true;
      button.disabled = true;
      button.classList.add('loading');

      try {
        if (action === 'add-gift') {
          await this.#addGiftToCart(variantId, button);
        } else if (action === 'remove-gift') {
          await this.#removeGiftFromCart(variantId, button);
        }
      } catch (error) {
        console.error('Gift action error:', error);
        button.disabled = false;
        button.classList.remove('loading');
      } finally {
        this.#operationInProgress = false;
      }
    }

    async #addGiftToCart(variantId, button) {
      const cartData = await fetch('/cart.js').then((r) => r.json());

      let cartSubtotal = 0;
      let creditSpent = 0;
      let giftCount = 0;

      for (const item of cartData.items) {
        if (item.properties?.['__free_gift'] === 'true') {
          const storedCost = parseInt(item.properties?.['_gift_credit_cost'], 10);
          creditSpent += (storedCost > 0 ? storedCost : Math.floor(item.price / 3)) * item.quantity;
          giftCount += item.quantity;
        } else {
          cartSubtotal += item.original_line_price;
        }
      }

      if (cartSubtotal < this.#giftThreshold) {
        button.disabled = false;
        button.classList.remove('loading');
        return;
      }

      // Max gifts gate
      if (giftCount >= this.#maxGifts) {
        button.disabled = false;
        button.classList.remove('loading');
        return;
      }

      // Credit budget: cart_total_cents * credit_percent / 100
      const creditBudget = Math.floor(cartSubtotal * this.#creditPercent / 100);
      const giftCreditCost = parseInt(button.dataset.giftCreditCost, 10) || Math.floor((parseInt(button.dataset.variantPrice, 10) || 0) / 3);

      if (giftCreditCost > creditBudget - creditSpent) {
        button.disabled = false;
        button.classList.remove('loading');
        return;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: {
            __free_gift: 'true',
            _gift_credit_cost: String(giftCreditCost),
          },
        }),
      });

      if (response.ok) {
        const cartData = await response.json();

        button.classList.remove('loading');
        button.classList.add('added');

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cartData,
              sourceId: 'cart-free-gifts',
              data: {
                source: 'cart-free-gifts',
                variantId: variantId,
              },
            },
          })
        );
      } else {
        button.disabled = false;
        button.classList.remove('loading');
      }
    }

    async #removeGiftFromCart(variantId, button) {
      const cartData = await fetch('/cart.js').then((r) => r.json());

      const giftItem = cartData.items.find(
        (item) =>
          String(item.variant_id) === String(variantId) &&
          item.properties?.['__free_gift'] === 'true'
      );

      if (!giftItem) {
        button.disabled = false;
        button.classList.remove('loading');
        return;
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0,
        }),
      });

      if (response.ok) {
        const updatedCart = await response.json();

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: updatedCart,
              sourceId: 'cart-free-gifts',
              data: {
                source: 'cart-free-gifts',
                variantId: variantId,
              },
            },
          })
        );
      } else {
        button.disabled = false;
        button.classList.remove('loading');
      }
    }

    // Remove out-of-stock gift line items flagged server-side (data-oos-gift-keys).
    // Liquid is the source of truth for which in-cart gift is OOS; JS just removes them.
    async #removeFlaggedGifts() {
      const keys = (this.dataset.oosGiftKeys || '').split(',').filter(Boolean);
      for (const key of keys) {
        try {
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ id: key, quantity: 0 }),
          });
          if (!res.ok) continue;
          const updatedCart = await res.json();
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: updatedCart,
                sourceId: 'cart-free-gifts',
                data: {
                  source: 'cart-free-gifts',
                  autoRemove: true,
                },
              },
            })
          );
        } catch (e) {
          console.error('Failed to remove out-of-stock gift:', e);
        }
      }
    }

    async #handleCartUpdate(event) {
      const source = event.detail?.data?.source;
      if (source === 'cart-free-gifts') {
        return;
      }

      // Remove backordered / out-of-stock gifts flagged by the latest server render
      await this.#removeFlaggedGifts();

      try {
        const cartResponse = await fetch('/cart.js');
        if (!cartResponse.ok) return;
        const cartData = await cartResponse.json();

        let cartSubtotal = 0;
        const giftItems = [];
        let giftCount = 0;

        for (const item of cartData.items) {
          if (item.properties?.['__free_gift'] === 'true') {
            giftItems.push(item);
            giftCount += item.quantity;
          } else {
            cartSubtotal += item.original_line_price;
          }
        }

        // Remove all gifts if below threshold
        if (cartSubtotal < this.#giftThreshold) {
          for (const gift of giftItems) {
            try {
              const freshCart = await fetch('/cart.js').then((r) => r.json());
              const item = freshCart.items.find(
                (j) =>
                  String(j.variant_id) === String(gift.variant_id) &&
                  j.properties?.['__free_gift'] === 'true'
              );
              if (!item) continue;

              const removeResponse = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({ id: item.key, quantity: 0 }),
              });

              if (removeResponse.ok) {
                const updatedCart = await removeResponse.json();
                document.dispatchEvent(
                  new CustomEvent('cart:update', {
                    bubbles: true,
                    detail: {
                      resource: updatedCart,
                      sourceId: 'cart-free-gifts',
                      data: {
                        source: 'cart-free-gifts',
                        autoRemove: true,
                      },
                    },
                  })
                );
              }
            } catch (e) {
              console.error('Failed to remove gift:', e);
            }
          }
          return;
        }

        // Auto-remove excess gifts beyond max count (LIFO)
        for (let i = giftItems.length - 1; i >= 0 && giftCount > this.#maxGifts; i--) {
          try {
            const freshCart = await fetch('/cart.js').then((r) => r.json());
            const item = freshCart.items.find(
              (j) =>
                String(j.variant_id) === String(giftItems[i].variant_id) &&
                j.properties?.['__free_gift'] === 'true'
            );
            if (!item) continue;

            const removeResponse = await fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify({ id: item.key, quantity: 0 }),
            });

            if (removeResponse.ok) {
              giftCount -= giftItems[i].quantity;
              const updatedCart = await removeResponse.json();
              document.dispatchEvent(
                new CustomEvent('cart:update', {
                  bubbles: true,
                  detail: {
                    resource: updatedCart,
                    sourceId: 'cart-free-gifts',
                    data: {
                      source: 'cart-free-gifts',
                      autoRemove: true,
                    },
                  },
                })
              );
            }
          } catch (e) {
            console.error('Failed to remove excess gift:', e);
          }
        }

        // Credit-based auto-removal: LIFO until within budget
        const creditBudget = Math.floor(cartSubtotal * this.#creditPercent / 100);
        let creditSpent = giftItems.reduce((s, g) => {
          const storedCost = parseInt(g.properties?.['_gift_credit_cost'], 10);
          return s + (storedCost > 0 ? storedCost : Math.floor(g.price / 3)) * g.quantity;
        }, 0);

        for (let i = giftItems.length - 1; i >= 0 && creditSpent > creditBudget; i--) {
          try {
            const freshCart = await fetch('/cart.js').then((r) => r.json());
            const item = freshCart.items.find(
              (j) =>
                String(j.variant_id) === String(giftItems[i].variant_id) &&
                j.properties?.['__free_gift'] === 'true'
            );
            if (!item) continue;

            const removeResponse = await fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify({ id: item.key, quantity: 0 }),
            });

            if (removeResponse.ok) {
              const removedCost = parseInt(giftItems[i].properties?.['_gift_credit_cost'], 10);
              creditSpent -= (removedCost > 0 ? removedCost : Math.floor(giftItems[i].price / 3)) * giftItems[i].quantity;
              const updatedCart = await removeResponse.json();
              document.dispatchEvent(
                new CustomEvent('cart:update', {
                  bubbles: true,
                  detail: {
                    resource: updatedCart,
                    sourceId: 'cart-free-gifts',
                    data: {
                      source: 'cart-free-gifts',
                      autoRemove: true,
                    },
                  },
                })
              );
            }
          } catch (e) {
            console.error('Failed to remove gift:', e);
          }
        }
      } catch (error) {
        console.error('Error checking gift eligibility:', error);
      }
    }
    #sortGiftCards() {
      const mode = this.dataset.giftSort || 'threshold';
      if (mode === 'default') return;

      const scroll = this.querySelector('.cart-free-gifts__scroll');
      if (!scroll) return;

      const cards = [...scroll.children];
      if (cards.length < 2) return;

      // Determine group order once before sorting
      let needFirst = true;
      if (mode === 'threshold') {
        const threshold = parseInt(this.dataset.giftSortThreshold) || 2000;
        const disabledCards = scroll.querySelectorAll('.cart-free-gift-card--disabled[data-spend-more]');
        const gaps = [...disabledCards].map(c => parseInt(c.dataset.spendMore) || Infinity);
        const smallestGap = gaps.length ? Math.min(...gaps) : Infinity;
        if (smallestGap > threshold) needFirst = false;
      }

      cards.sort((a, b) => {
        const ga = this.#cardGroup(a, needFirst);
        const gb = this.#cardGroup(b, needFirst);
        if (ga !== gb) return ga - gb;

        // Same group sub-sort
        if (a.classList.contains('cart-free-gift-card--disabled')) {
          return (parseInt(a.dataset.giftCreditCost) || 0) - (parseInt(b.dataset.giftCreditCost) || 0);
        }
        if (!a.classList.contains('cart-free-gift-card--in-cart')) {
          return (parseInt(a.dataset.variantPrice) || 0) - (parseInt(b.dataset.variantPrice) || 0);
        }
        return 0;
      });

      this.#observer?.disconnect();
      cards.forEach(c => scroll.appendChild(c));
      this.#observer?.observe(this, { childList: true, subtree: true, characterData: true });
    }

    #cardGroup(card, needFirst) {
      if (card.classList.contains('cart-free-gift-card--in-cart')) return 0;
      const isDisabled = card.classList.contains('cart-free-gift-card--disabled');
      if (needFirst) return isDisabled ? 1 : 2;
      return isDisabled ? 2 : 1;
    }
  }
  customElements.define('cart-free-gifts', CartFreeGifts);
}
